import React, { useState } from "react";
import { KeyRound, X } from "lucide-react";
import { toast } from "react-toastify";
import { accountApi, accountErr } from "../../api/account/accountApi";
import { createCredential, isWebAuthnSupported, webauthnErr } from "../../utils/webauthn";

interface AddSecurityKeyModalProps {
  onClose: () => void;
  onDone: () => void;
}

/**
 * Enrol a security key / device authenticator (WebAuthn) without leaving the
 * CMS. authz-api registers it with Zitadel on our RP domain, so it works at
 * the in-app login on every org subdomain.
 */
const AddSecurityKeyModal: React.FC<AddSecurityKeyModalProps> = ({ onClose, onDone }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const supported = isWebAuthnSupported();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError("Give the key a name so you can recognise it later.");
    setBusy(true);
    setError("");
    try {
      const begin = await accountApi.u2fBegin();
      let credential: any;
      try {
        credential = await createCredential(begin.data.options);
      } catch (err: any) {
        setError(webauthnErr(err, "Could not create the credential"));
        return;
      }
      await accountApi.u2fVerify(begin.data.u2f_id, name.trim(), credential);
      toast.success(`Security key "${name.trim()}" added`);
      onDone();
    } catch (err) {
      setError(accountErr(err, "Could not add the security key"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="acct-modal-overlay" onClick={onClose}>
      <form className="acct-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="acct-modal__head">
          <h3>Add security key</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <p>
          Works with hardware keys (YubiKey, Titan) and with this device's fingerprint,
          face unlock or PIN. Name it, then follow your browser's prompt.
        </p>
        {!supported && (
          <p className="acct-form-error">This browser or device does not support security keys.</p>
        )}
        <div className="acct-form">
          <label className="full">
            Key name
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. YubiKey, Work laptop, Phone"
              maxLength={100}
            />
          </label>
        </div>
        {error && <p className="acct-form-error">{error}</p>}
        <div className="acct-actions">
          <button type="button" className="acct-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="acct-btn acct-btn--primary" disabled={busy || !supported}>
            <KeyRound size={14} /> {busy ? "Waiting for key..." : "Add key"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddSecurityKeyModal;
